import React, { useState } from "react";

import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ExternalLink,
  Factory,
  Handshake,
  Lightbulb,
  Mail,
  MapPin,
  Network,
  Rocket,
  Send,
  Sparkles,
  Users,
} from "lucide-react";

import "./HubAcre.css";
import PartnersRuler from "../components/PartnersRuler";

const FORM_ENDPOINT = "/api/hub/atendimentos";

function HubAcre() {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();

    setSending(true);
    setMessage("");

    try {
      const form = event.currentTarget;
      const formData = new FormData(form);

      const payload = {};

      for (const [key, value] of formData.entries()) {
        payload[key] = value;
      }

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.detail ||
          "Não foi possível enviar sua solicitação."
        );
      }

      setMessage(
        `Solicitação enviada com sucesso! Seu protocolo é ${data.protocolo}. Salve esse número para acompanhamento.`
      );

      form.reset();
    } catch (error) {
      setMessage(
        error?.message ||
        "Não foi possível enviar sua solicitação."
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="hub-page notranslate" translate="no">

      <header className="hub-nav">
        <div className="hub-container hub-nav-inner">
          <a href="/hubacre" className="hub-nav-logo">
            <img
              src="/hubacre/logo-horizontal.png"
              alt="HUB Acre"
            />
          </a>

          <nav>
            <a href="#sobre">Sobre</a>
            <a href="#programas">Programas</a>
            <a href="/hubacre/agenda">Agenda</a>
            <a href="#atendimento">Atendimento</a>

            <a
              href="#atendimento"
              className="hub-nav-cta"
            >
              Falar com o HUB
            </a>
          </nav>
        </div>
      </header>

      <section className="hub-hero">
        <div className="hub-hero-pattern hub-pattern-one" />
        <div className="hub-hero-pattern hub-pattern-two" />

        <div className="hub-container hub-hero-grid">
          <div className="hub-hero-copy">
            <span className="hub-eyebrow">
              INOVAÇÃO • CONEXÃO • DESENVOLVIMENTO
            </span>

            <h1>
              Um espaço para
              <br />
              <strong>conectar ideias</strong>
              <br />
              e transformar
              <br />
              oportunidades.
            </h1>

            <p>
              O HUB Acre conecta pessoas, projetos,
              empresas, instituições e iniciativas que
              querem inovar, colaborar e construir novas
              soluções para o desenvolvimento do Acre.
            </p>

            <div className="hub-hero-actions">
              <a
                href="#atendimento"
                className="hub-btn hub-btn-black"
              >
                Quero atendimento
                <ArrowRight />
              </a>

              <a
                href="#programas"
                className="hub-btn hub-btn-white"
              >
                Conhecer programas
              </a>
            </div>
          </div>

          <div className="hub-hero-brand">
            <img
              src="/hubacre/logo-vertical.png"
              alt="HUB Acre"
            />

            <div className="hub-hero-tagline">
              <span>INOVE</span>
              <strong>CONECTE</strong>
              <span>TRANSFORME</span>
            </div>
          </div>
        </div>
      </section>

      <section className="hub-highlight">
        <div className="hub-container hub-highlight-grid">
          <div>
            <Network />
            <strong>Conexão</strong>
            <span>
              Pessoas, ideias e oportunidades.
            </span>
          </div>

          <div>
            <Lightbulb />
            <strong>Inovação</strong>
            <span>
              Novas soluções para desafios reais.
            </span>
          </div>

          <div>
            <Handshake />
            <strong>Colaboração</strong>
            <span>
              Fortalecimento de redes e parcerias.
            </span>
          </div>

          <div>
            <Rocket />
            <strong>Desenvolvimento</strong>
            <span>
              Apoio para ideias avançarem.
            </span>
          </div>
        </div>
      </section>

      <section
        id="sobre"
        className="hub-section hub-about"
      >
        <div className="hub-container hub-two-columns">
          <div>
            <span className="hub-section-label">
              SOBRE O HUB
            </span>

            <h2>
              Conectar.
              <br />
              Colaborar.
              <br />
              <strong>Transformar.</strong>
            </h2>
          </div>

          <div className="hub-about-copy">
            <p>
              O HUB Acre é um ambiente de articulação do
              ecossistema de inovação, criado para
              aproximar pessoas, iniciativas, empresas,
              poder público, academia e organizações.
            </p>

            <p>
              Aqui, ideias encontram conexões,
              conhecimento encontra oportunidade e
              projetos encontram caminhos para avançar.
            </p>

            <div className="hub-concept">
              <img
                src="/hubacre/simbolo-circular.png"
                alt=""
              />

              <div>
                <strong>
                  Uma rede que cresce a partir das conexões.
                </strong>

                <p>
                  O símbolo do HUB Acre representa uma
                  rede de nós interligados: colaboração,
                  integração e construção coletiva.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="hub-section hub-who">
        <div className="hub-container">
          <span className="hub-section-label">
            PARA QUEM É
          </span>

          <h2>
            O HUB é um ponto de
            <br />
            encontro para quem
            <br />
            <strong>quer construir.</strong>
          </h2>

          <div className="hub-audiences">
            <article>
              <Users />
              <h3>Empreendedores</h3>
              <p>
                Para quem está criando, validando ou
                desenvolvendo novos negócios.
              </p>
            </article>

            <article>
              <Factory />
              <h3>Empresas</h3>
              <p>
                Para organizações interessadas em
                inovação, tecnologia e novas conexões.
              </p>
            </article>

            <article>
              <Sparkles />
              <h3>Makers e criativos</h3>
              <p>
                Para quem transforma conhecimento e
                criatividade em soluções.
              </p>
            </article>

            <article>
              <Building2 />
              <h3>Instituições</h3>
              <p>
                Para organizações públicas, privadas,
                acadêmicas e sociais.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        id="programas"
        className="hub-section hub-programs"
      >
        <div className="hub-container">
          <span className="hub-section-label">
            PROGRAMAS E OPORTUNIDADES
          </span>

          <div className="hub-program-heading">
            <h2>
              Oportunidades
              <br />
              <strong>abertas.</strong>
            </h2>

            <p>
              Conheça os programas de aceleração
              atualmente disponíveis no HUB Acre.
            </p>
          </div>

          <div className="hub-program-grid">

            <a
              href="/aceleracaonfsb"
              className="hub-program-card hub-program-nfsb"
            >
              <div>
                <span>
                  ACELERAÇÃO
                </span>

                <h3>
                  Nunca Foi
                  <br />
                  Só Batom
                </h3>

                <p>
                  Conheça o programa, consulte o edital
                  e faça a inscrição.
                </p>
              </div>

              <div className="hub-program-link">
                Ver programa
                <ExternalLink />
              </div>
            </a>

            <a
              href="/programadomakeraindustria"
              className="hub-program-card hub-program-maker"
            >
              <div>
                <span>
                  ACELERAÇÃO
                </span>

                <h3>
                  Do Maker
                  <br />
                  à Indústria
                </h3>

                <p>
                  Modelagem, prototipagem, fabricação
                  digital e desenvolvimento de produtos.
                </p>
              </div>

              <div className="hub-program-link">
                Ver programa
                <ExternalLink />
              </div>
            </a>

          </div>
        </div>
      </section>

      <section className="hub-orange-callout">
        <div className="hub-container">
          <Network />

          <h2>
            Tem uma ideia,
            <br />
            projeto ou demanda?
          </h2>

          <p>
            Fale com o HUB Acre. Nosso atendimento ajuda
            a identificar possibilidades de conexão,
            programas, parceiros e oportunidades.
          </p>

          <a
            href="#atendimento"
            className="hub-btn hub-btn-black"
          >
            Solicitar atendimento
            <ArrowRight />
          </a>
        </div>
      </section>

      <section
        id="atendimento"
        className="hub-section hub-service"
      >
        <div className="hub-container hub-service-grid">

          <div className="hub-service-intro">
            <span className="hub-section-label">
              ATENDIMENTO
            </span>

            <h2>
              Vamos
              <br />
              <strong>conversar?</strong>
            </h2>

            <p>
              Conte brevemente quem você é e como o HUB
              Acre pode ajudar. Sua solicitação será
              registrada e receberá um protocolo.
            </p>

            <div className="hub-contact-card">
              <Mail />

              <div>
                <strong>
                  Atendimento HUB Acre
                </strong>

                <span>
                  Preencha o formulário ao lado.
                </span>
              </div>
            </div>
          </div>

          <form
            className="hub-form"
            onSubmit={handleSubmit}
          >
            <div className="hub-form-grid">

              <label className="hub-full">
                Nome completo *
                <input
                  name="nome"
                  required
                />
              </label>

              <label>
                WhatsApp *
                <input
                  name="telefone"
                  type="tel"
                  required
                />
              </label>

              <label>
                E-mail *
                <input
                  name="email"
                  type="email"
                  required
                />
              </label>

              <label>
                Município *
                <input
                  name="municipio"
                  required
                />
              </label>

              <label>
                Você é / representa *
                <select
                  name="perfil"
                  required
                >
                  <option value="">
                    Selecione
                  </option>

                  <option value="empreendedor">
                    Empreendedor(a)
                  </option>

                  <option value="empresa">
                    Empresa
                  </option>

                  <option value="estudante">
                    Estudante
                  </option>

                  <option value="maker">
                    Maker / profissional criativo
                  </option>

                  <option value="instituicao_publica">
                    Instituição pública
                  </option>

                  <option value="instituicao_privada">
                    Instituição privada
                  </option>

                  <option value="organizacao_social">
                    Organização social
                  </option>

                  <option value="pesquisador">
                    Pesquisador(a) / academia
                  </option>

                  <option value="outro">
                    Outro
                  </option>
                </select>
              </label>

              <label className="hub-full">
                Organização, empresa ou projeto
                <input
                  name="organizacao"
                />
              </label>

              <label className="hub-full">
                Qual o principal motivo do contato? *
                <select
                  name="assunto"
                  required
                >
                  <option value="">
                    Selecione
                  </option>

                  <option value="programas">
                    Programas e editais
                  </option>

                  <option value="empreendedorismo">
                    Empreendedorismo
                  </option>

                  <option value="inovacao">
                    Inovação
                  </option>

                  <option value="prototipagem">
                    Prototipagem / fabricação digital
                  </option>

                  <option value="parceria">
                    Parcerias
                  </option>

                  <option value="espaco">
                    Uso do espaço / infraestrutura
                  </option>

                  <option value="evento">
                    Eventos e atividades
                  </option>

                  <option value="outro">
                    Outro
                  </option>
                </select>
              </label>

              <label className="hub-full">
                Conte um pouco sobre sua demanda *
                <textarea
                  name="descricao"
                  rows="7"
                  required
                  placeholder="Descreva sua ideia, projeto, necessidade ou oportunidade..."
                />
              </label>

              <label className="hub-full hub-check">
                <input
                  type="checkbox"
                  name="consentimento"
                  value="sim"
                  required
                />

                <span>
                  Autorizo o uso dos dados informados
                  exclusivamente para atendimento,
                  comunicação e encaminhamento desta
                  solicitação.
                </span>
              </label>

            </div>

            <div className="hub-form-footer">
              <div>
                <strong>
                  Após enviar, salve seu protocolo.
                </strong>

                <span>
                  Ele será a identificação do seu
                  atendimento.
                </span>
              </div>

              <button
                type="submit"
                className="hub-btn hub-btn-orange"
                disabled={sending}
              >
                {sending
                  ? "Enviando..."
                  : "Enviar solicitação"}

                {!sending && <Send />}
              </button>
            </div>

            {message && (
              <div className="hub-form-message">
                {message}
              </div>
            )}
          </form>

        </div>
      </section>

      <footer className="hub-footer">
        <div className="hub-container">
          <img
            src="/hubacre/logo-vertical.png"
            alt="HUB Acre"
          />

          <p>
            Conectando ideias. Construindo futuro.
          </p>

          <a href="#atendimento">
            Falar com o HUB
          </a>
        </div>
      </footer>

      <PartnersRuler />

    </main>
  );
}

export default HubAcre;
